"use client";
import { IconCryptoWallet } from "@central-icons-react/square-outlined-radius-0-stroke-1.5";
import { IconChevronRightOutline18, IconCircleCheckOutline18 } from "nucleo-ui-outline-18";

import { useChainInfoMap } from "@/app/(v2)/workflows/_hooks/useChainInfo";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { TokenWithChainIcon } from "@/components/ui/token-with-chain-icon";
import { useDebouncedRelayTokenSearch } from "@/hooks/useRelayTokenSearch";
import { useSimBalance, useSimTokenInfo } from "@/hooks/useSimTokenInfo";
import { addressesMatch, isNativeToken, isNativeTokenAddress, NATIVE_TOKEN_LITERAL } from "@/lib/tokens/nativeToken";
import { formatAddress } from "@/lib/utils";
import { PixelLoader } from "@/components/ui/pixel-loader";
import { formatUnits } from "viem";
import type { ReactNode } from "react";
import { memo, useCallback, useRef, useMemo, useState } from "react";

interface TokenChainDropdownProps {
  tokenAddress: string;
  chainId: number;
  onTokenChange: (address: string, chainId: number, symbol: string, decimals: number) => void;
  popoverModal?: boolean;
  /** Workflow wallet address — shows wallet tokens sorted by USD value at top */
  workflowWalletAddress?: string;
  /** When true, hides native coins (ETH, etc.) from both wallet section and search results */
  excludeNative?: boolean;
  /** Render prop — receives the pill button, lets parent control layout */
  children: (pill: ReactNode) => ReactNode;
}

export default memo(function TokenChainDropdown({
  tokenAddress,
  chainId,
  onTokenChange,
  popoverModal = false,
  workflowWalletAddress,
  excludeNative = false,
  children,
}: TokenChainDropdownProps) {
  const [containerEl, setContainerEl] = useState<HTMLDivElement | null>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  // Selected token metadata
  const { data: selectedTokenResp } = useSimTokenInfo(tokenAddress, chainId, !!tokenAddress);
  const selectedToken = selectedTokenResp?.tokens?.[0];

  // Chain info for the pill
  const { data: selectedChainMap } = useChainInfoMap(chainId ? [chainId] : []);
  const selectedChainInfo = chainId ? selectedChainMap.get(chainId) : undefined;

  // Token search (all EVM chains)
  const {
    tokens: allTokens,
    isLoading: isSearching,
    setSearchTerm: setRelaySearch,
  } = useDebouncedRelayTokenSearch(undefined, 50);
  // Hide native coins when the template requires an ERC20 contract address.
  // Relay results carry `metadata.isNative`; wallet balances only expose an
  // address, so the two call sites compute `native` with the appropriate helper.
  const isHiddenByExcludeNative = useCallback((native: boolean) => excludeNative && native, [excludeNative]);
  const tokens = useMemo(
    () => allTokens.filter(t => t.vmType === "evm" && !isHiddenByExcludeNative(isNativeToken(t))),
    [allTokens, isHiddenByExcludeNative],
  );

  const handleSearch = (term: string) => {
    setSearchTerm(term);
    setRelaySearch(term);
  };

  const closeDropdown = () => {
    setOpen(false);
    setSearchTerm("");
    setRelaySearch("");
  };

  // Wallet balances — sorted by USD value desc
  const { data: walletBalanceData } = useSimBalance(workflowWalletAddress, undefined, !!workflowWalletAddress);
  const walletTokens = useMemo(() => {
    if (!walletBalanceData?.balances) return [];
    return walletBalanceData.balances
      .filter(b => (b.value_usd ?? 0) > 0)
      .filter(b => !isHiddenByExcludeNative(isNativeTokenAddress(b.address)))
      .sort((a, b) => (b.value_usd ?? 0) - (a.value_usd ?? 0))
      .map(b => ({
        chainId: b.chain_id,
        address: isNativeTokenAddress(b.address) ? "0x0000000000000000000000000000000000000000" : b.address,
        symbol: b.symbol,
        name: b.name || b.symbol,
        decimals: b.decimals,
        metadata: { logoURI: b.token_metadata?.logo, verified: true, isNative: isNativeTokenAddress(b.address) },
        balance: b.amount,
        value_usd: b.value_usd,
      }));
  }, [walletBalanceData, isHiddenByExcludeNative]);

  // Filter wallet tokens by search term
  const filteredWalletTokens = useMemo(() => {
    if (!searchTerm) return walletTokens;
    const q = searchTerm.toLowerCase();
    return walletTokens.filter(
      t =>
        t.symbol.toLowerCase().includes(q) || t.name.toLowerCase().includes(q) || t.address.toLowerCase().includes(q),
    );
  }, [walletTokens, searchTerm]);

  // Chain info for search results + wallet tokens
  const walletChainIds = useMemo(() => walletTokens.map(t => t.chainId), [walletTokens]);
  const resultChainIds = useMemo(
    () => [...new Set([...tokens.map(t => t.chainId), ...walletChainIds])],
    [tokens, walletChainIds],
  );
  const { data: chainInfoMap } = useChainInfoMap(resultChainIds);

  const pill = (
    <button
      type="button"
      onClick={() => setOpen(true)}
      // Content is grouped into exactly two top-level children so callers can
      // apply `justify-between` (TokenChainInput full-row) without the icon,
      // text, and chevron spreading across three tracks.
      className="hover:bg-ds-surface-hover dark:hover:bg-ds-neutral-700 flex shrink-0 cursor-pointer items-center justify-between gap-2 rounded-lg bg-transparent py-1.5 pl-2 pr-2.5 outline-none transition-colors focus-visible:ring-2 focus-visible:ring-blue-500/30 focus-visible:ring-offset-2 focus-visible:ring-offset-transparent"
    >
      <span className="flex min-w-0 items-center gap-2">
        {selectedToken ? (
          <>
            <TokenWithChainIcon
              logoUrl={selectedToken.logo}
              symbol={selectedToken.symbol}
              chainId={chainId}
              tokenAddress={tokenAddress}
              tokenSize={24}
              chainIconUrl={selectedChainInfo?.iconUrl}
              chainName={selectedChainInfo?.name}
              chainIconSize={12}
            />
            <span className="flex min-w-0 items-baseline gap-1">
              <span className="text-ds-text-primary truncate text-sm font-semibold">{selectedToken.symbol}</span>
              {selectedChainInfo && (
                <span className="text-ds-text-disabled dark:text-ds-text-muted text-2xs truncate">
                  {selectedChainInfo.name}
                </span>
              )}
            </span>
          </>
        ) : (
          <span className="text-ds-text-disabled px-1 text-sm">Select token</span>
        )}
      </span>
      <IconChevronRightOutline18 className="text-ds-text-disabled h-3.5 w-3.5 shrink-0" />
    </button>
  );

  return (
    <div ref={setContainerEl}>
      <Popover
        open={open}
        onOpenChange={next => {
          if (!next) closeDropdown();
        }}
        modal={popoverModal}
      >
        <PopoverTrigger asChild>{children(pill)}</PopoverTrigger>

        <PopoverContent
          className="border-ds-border-default bg-ds-surface-card w-[var(--radix-popover-trigger-width)] p-0 shadow-xl"
          align="start"
          sideOffset={8}
          container={popoverModal ? (containerEl ?? undefined) : undefined}
          onOpenAutoFocus={e => {
            e.preventDefault();
            searchInputRef.current?.focus({ preventScroll: true });
          }}
        >
          {/* Search */}
          <div className="border-ds-border-default border-b p-3">
            <Input
              ref={searchInputRef}
              type="text"
              value={searchTerm}
              onChange={e => handleSearch(e.target.value)}
              placeholder="Search token or paste address..."
              className="text-sm"
            />
          </div>

          {/* Results */}
          <div className="max-h-64 overflow-y-auto p-1">
            {/* Wallet tokens section */}
            {filteredWalletTokens.length > 0 && (
              <div>
                <div className="dark:bg-ds-neutral-900 flex items-center gap-1.5 bg-zinc-50 px-3 py-2">
                  <IconCryptoWallet className="text-ds-text-muted dark:text-ds-text-subtle h-3 w-3" />
                  <p className="text-ds-text-muted dark:text-ds-text-subtle text-xs font-semibold uppercase tracking-wide">
                    In your wallet
                  </p>
                </div>
                {filteredWalletTokens.map((token, idx) => {
                  const ci = chainInfoMap.get(token.chainId);
                  const isSelected = addressesMatch(token.address, tokenAddress) && token.chainId === chainId;
                  const humanBalance = token.balance
                    ? parseFloat(formatUnits(BigInt(token.balance), token.decimals))
                    : 0;
                  return (
                    <button
                      key={`wallet-${token.chainId}-${token.address}-${idx}`}
                      type="button"
                      onClick={() => {
                        const addr = token.metadata.isNative ? NATIVE_TOKEN_LITERAL : token.address;
                        onTokenChange(addr, token.chainId, token.symbol, token.decimals);
                        closeDropdown();
                      }}
                      className={`hover:bg-ds-surface-hover flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left transition-colors ${
                        isSelected ? "bg-blue-50 dark:bg-blue-900/20" : ""
                      }`}
                    >
                      <TokenWithChainIcon
                        logoUrl={token.metadata.logoURI}
                        symbol={token.symbol}
                        chainId={token.chainId}
                        tokenAddress={token.address}
                        tokenSize={28}
                        chainIconUrl={ci?.iconUrl}
                        chainName={ci?.name}
                        chainIconSize={14}
                      />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1.5">
                          <span className="text-ds-text-primary text-sm font-semibold">{token.symbol}</span>
                          {ci && <span className="text-ds-text-disabled text-xs">{ci.name}</span>}
                        </div>
                        <p className="text-xs text-blue-600 dark:text-blue-400">
                          {humanBalance.toLocaleString(undefined, { maximumFractionDigits: 6 })} {token.symbol}
                          {token.value_usd
                            ? ` · $${token.value_usd.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                            : ""}
                        </p>
                      </div>
                      {isSelected && <IconCircleCheckOutline18 className="h-4 w-4 shrink-0 text-blue-500" />}
                    </button>
                  );
                })}
                {(tokens.length > 0 || isSearching) && (
                  <div className="bg-ds-neutral-200 dark:bg-ds-neutral-700 my-1 h-px" />
                )}
              </div>
            )}

            {/* Search results */}
            {isSearching ? (
              <div className="text-ds-text-disabled flex items-center justify-center gap-2 py-6 text-sm">
                <PixelLoader size="sm" />
                Searching...
              </div>
            ) : tokens.length === 0 && filteredWalletTokens.length === 0 ? (
              <div className="text-ds-text-disabled py-6 text-center text-sm">
                {searchTerm ? "No tokens found" : "Type to search tokens"}
              </div>
            ) : (
              tokens.slice(0, 30).map(token => {
                const ci = chainInfoMap.get(token.chainId);
                const isSelected = addressesMatch(token.address, tokenAddress) && token.chainId === chainId;
                return (
                  <button
                    key={`${token.chainId}-${token.address}`}
                    type="button"
                    onClick={() => {
                      onTokenChange(token.address, token.chainId, token.symbol, token.decimals);
                      closeDropdown();
                    }}
                    className={`hover:bg-ds-surface-hover flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left transition-colors ${
                      isSelected ? "bg-blue-50 dark:bg-blue-900/20" : ""
                    }`}
                  >
                    <TokenWithChainIcon
                      logoUrl={token.metadata.logoURI}
                      symbol={token.symbol}
                      chainId={token.chainId}
                      tokenAddress={token.address}
                      tokenSize={28}
                      chainIconUrl={ci?.iconUrl}
                      chainName={ci?.name}
                      chainIconSize={14}
                    />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5">
                        <span className="text-ds-text-primary text-sm font-semibold">{token.symbol}</span>
                        {ci && <span className="text-ds-text-disabled text-xs">{ci.name}</span>}
                      </div>
                      <p className="text-ds-text-disabled truncate text-xs">
                        {token.name}
                        {!isNativeTokenAddress(token.address) && ` · ${formatAddress(token.address, 4, 4)}`}
                      </p>
                    </div>
                    {isSelected && <IconCircleCheckOutline18 className="h-4 w-4 shrink-0 text-blue-500" />}
                  </button>
                );
              })
            )}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
});
